import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

// Standard UTM tags (also the ones we re-append to the URL on navigation).
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Ad-network click identifiers — useful for attributing a lead back to the exact ad click.
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'];
const CAMPAIGN_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS];
const STORAGE_KEY = 'utm_params';

// ─── Storage helpers ──────────────────────────────────────────────────────────

/**
 * Captures the campaign context of the visit into sessionStorage. Uses FIRST-TOUCH
 * attribution: the source that opened the session is kept as-is and not overwritten
 * by later internal navigations. A later hit that carries new campaign params does
 * replace it (a genuinely new campaign click). Values are stored verbatim.
 */
export function captureUtmParams() {
    const params = new URLSearchParams(window.location.search);
    const existing = getCampaignParams();

    const hit = {};
    CAMPAIGN_KEYS.forEach(key => { const v = params.get(key); if (v) hit[key] = v; });

    const hasNewCampaign = Object.keys(hit).length > 0;
    // Nothing new and already captured → leave first-touch record untouched.
    if (!hasNewCampaign && existing.captured_at) return;

    const record = {
        ...(hasNewCampaign ? hit : existing), // fresh campaign replaces; else keep prior tags
        page_url: existing.page_url || window.location.href,
        referrer: existing.referrer || document.referrer || '',
        captured_at: existing.captured_at || new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
}

/** Returns the stored campaign params object. Always safe — returns {} if none captured. */
export function getCampaignParams() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (_) { return {}; }
}

/** Back-compat alias — same data as getCampaignParams(). */
export const getUtmParams = getCampaignParams;

/** Returns the original landing URL with UTMs, or the current URL as fallback. */
export function getLandingUrl() {
    return getCampaignParams().page_url || window.location.href;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Call once at app root.
 * Captures UTM params from the current URL into sessionStorage on first load.
 */
export default function useUtmCapture() {
    useEffect(() => { captureUtmParams(); }, []);
}

/**
 * Call once at app root (inside Router).
 * On every route change, silently re-appends stored UTM params to the URL
 * using replace:true so the back button isn't affected.
 * This means any link a user copies/shares will still carry the UTMs.
 */
export function useUtmPreserve() {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const stored = getUtmParams();
        if (!stored.utm_source) return; // nothing stored, nothing to do

        const current = new URLSearchParams(location.search);
        const alreadyHasUtm = UTM_KEYS.some(k => current.has(k));
        if (alreadyHasUtm) return; // UTMs already in URL

        // Append stored UTMs to the current URL without creating a history entry
        UTM_KEYS.forEach(k => { if (stored[k]) current.set(k, stored[k]); });
        navigate(
            { pathname: location.pathname, search: current.toString() },
            { replace: true }
        );
    }, [location.pathname]); // re-run on every page change
}
